import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search as SearchIcon, ChevronRight } from 'lucide-react';
import { searchPipeline } from '@/services/search.service.js';
import { searchDocuments } from '@/services/document-list.service.js';
import DocumentListRows from '@/components/account/DocumentListRows.jsx';
import TestBadge from '@/components/shared/TestBadge.jsx';
import { flattenRows, hrefForRow } from '@/lib/search-rows.js';

// /search — the "Accounts" tab destination. A page-level version
// of the search palette for broker discoverability; cmd-K and the
// TopBar icon remain the faster paths. Spec §5.1 lists this as
// "accounts/deals list (lightweight; nav target)" — V1 implements
// it as a search-as-you-type box rather than a static list (the
// active pipeline is what the broker actually wants to find).

export default function Search() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  // Test data is hidden unless asked for (David 2026-10-31); each test hit is badged, the documents' too.
  const [showTest, setShowTest] = useState(false);
  // SCREEN 7 (paperwork-1 D8, L1b-B3): the documents whose fields match every word (the server's token search).
  const [docs, setDocs] = useState({ loading: false, rows: [], total: 0, truncated: false, error: null });

  useEffect(() => {
    if (q.trim().length < 2) {
      setDocs({ loading: false, rows: [], total: 0, truncated: false, error: null });
      return;
    }
    let live = true;
    const t = setTimeout(async () => {
      setDocs((d) => ({ ...d, loading: true, error: null }));
      try {
        const r = await searchDocuments(q, { showTest });
        if (live) setDocs({ loading: false, rows: r?.documents || [], total: r?.total || 0, truncated: r?.truncated === true, error: null });
      } catch (e) {
        if (live) setDocs({ loading: false, rows: [], total: 0, truncated: false, error: e?.body?.error || e?.message || 'the server did not answer' });
      }
    }, 250);
    return () => { live = false; clearTimeout(t); };
  }, [q, showTest]);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await searchPipeline(q, { showTest });
        setResults(r?.results || []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, showTest]);

  return (
    <div className="mx-auto max-w-2xl p-4 md:p-6">
      <h1 className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">
        Accounts, deals &amp; documents
      </h1>
      <p className="mb-3 text-xs text-text-dim">Find a client or a deal by name, application number, tail or contact.</p>
      <div className="flex items-center gap-2 rounded-card border border-border bg-surface px-3">
        <SearchIcon size={16} strokeWidth={1.5} className="text-text-dim" />
        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Name, ND-APP-#####, N-number, contact, policy number…"
          className="h-12 flex-1 bg-transparent text-sm text-text outline-none placeholder:text-text-dim"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />
      </div>
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-dim">
          Tip: <kbd className="font-mono">⌘K</kbd> opens the same search from anywhere.
          Results are limited to deals currently in the active pipeline.
        </p>
        <label className="flex min-h-[36px] cursor-pointer items-center gap-2 text-xs text-text-muted" data-show-test-toggle>
          <input
            type="checkbox"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
            className="h-4 w-4 accent-[rgb(var(--c-warning))]"
          />
          Show test
        </label>
      </div>
      <p className="mt-1 text-xs text-text-dim">
        {showTest ? 'Test records are shown, each marked TEST.' : 'Test records are hidden.'}
      </p>

      <ul className="mt-4 space-y-1">
        {loading ? (
          <li className="px-3 py-4 text-center text-xs text-text-dim">Searching…</li>
        ) : results.length === 0 ? (
          <li className="px-3 py-4 text-center text-xs text-text-dim">
            {q.trim().length < 2
              ? 'Type at least 2 characters to search.'
              : 'No matches in the active pipeline.'}
          </li>
        ) : (
          // One derivation of each row and its link, shared with Cmd+K (lib/search-rows.js): an account result opens the
          // account, each of its terms opens that deal, a hit with no account opens the deal.
          flattenRows(results, true).map((row) => {
            const isTerm = row.kind === 'term';
            const r = isTerm ? row.t : row.r;
            const href = hrefForRow(row);
            return (
              <li key={row.key} className={isTerm ? 'pl-6' : undefined}>
                <Link
                  to={href}
                  data-search-row={row.kind}
                  data-search-href={href}
                  className="block rounded-card border border-border bg-surface p-3 hover:border-border-strong"
                >
                  <div className="flex items-center gap-2">
                    {isTerm ? <ChevronRight size={12} strokeWidth={1.5} className="shrink-0 text-text-dim" /> : null}
                    <span className={isTerm ? 'truncate text-[13px] text-text' : 'truncate text-sm font-medium text-text'}>{r.label}</span>
                    {r.is_test === true ? <TestBadge isTest /> : null}
                  </div>
                  {r.sublabel ? (
                    <div className="truncate text-[11px] text-text-dim">{r.sublabel}</div>
                  ) : null}
                </Link>
              </li>
            );
          })
        )}
      </ul>

      <section className="mt-6" data-document-search>
        <h2 className="mb-1 text-xs font-semibold uppercase tracking-[0.14em] text-text-muted">Documents</h2>
        <p className="mb-2 text-xs text-text-dim">Documents whose tail, policy number, account, kind, carrier, holder, endorsement date, original file or term years match every word you type.</p>
        {q.trim().length < 2 ? (
          <p className="text-xs text-text-dim">Type at least 2 characters to search documents.</p>
        ) : docs.loading ? (
          <p className="text-xs text-text-dim">Searching documents...</p>
        ) : docs.error ? (
          <p className="text-xs text-warning">Could not search the documents: {docs.error}</p>
        ) : (
          <>
            <DocumentListRows rows={docs.rows} showPolicy empty="No document matches every word." />
            {docs.truncated ? <p className="mt-1 text-xs text-text-dim">Showing {docs.rows.length} of {docs.total}. Add a word to narrow it.</p> : null}
          </>
        )}
      </section>
    </div>
  );
}
